import React, { useEffect } from 'react';

const Resume = () => {
  useEffect(() => {
    // Timeline animation on scroll
    const animateTimeline = () => {
      const timelineItems = document.querySelectorAll('.timeline-item');
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      timelineItems.forEach(item => {
        observer.observe(item);
      });
    };

    animateTimeline();
  }, []);

  return (
    <div className="resume-page">
      <section className="resume-header">
        <div className="container">
          <div className="resume-header-content">
            <div className="profile-photo">
              <img src="./images/russell-profile.jpg" alt="Russell Pope" />
            </div>
            <h1 className="resume-title">Russell Pope</h1>
            <p className="resume-subtitle">Program Manager & Freelance Web Developer</p>
            <p className="resume-summary">
              Experienced Program Manager and freelance web developer with 19+ years of expertise in technology-driven initiatives, 
              process improvement, and web development. Currently leading strategic technology projects for professional services while 
              maintaining a successful freelance practice specializing in Microsoft Power Platform solutions and custom web applications.
            </p>
            <div className="contact-info">
              <div className="contact-item">
                <i className="fas fa-envelope"></i>
                <span>Contact via LinkedIn</span>
              </div>
              <div className="contact-item">
                <i className="fas fa-map-marker-alt"></i>
                <span>Jacksonville, FL</span>
              </div>
              <div className="contact-item">
                <i className="fas fa-calendar"></i>
                <span>Since 2006</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="resume-content">
        <div className="container">
          <div className="section-title">
            <h2>Professional Journey</h2>
            <p>A timeline of my career progression and key achievements</p>
          </div>

          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-date">Mar 2025 - Present</div>
              <div className="timeline-content">
                <h3 className="job-title">Shared Services Program Manager</h3>
                <p className="job-description">
                  Leading complex, technology-driven initiatives that streamline business processes and enhance customer and employee experience in the Shared Services division.
                </p>
                <ul className="job-achievements">
                  <li>Lead strategic technology projects to modernize shared service delivery across the organization</li>
                  <li>Define success criteria, project scope, training materials, and post-implementation quality assurance metrics</li>
                  <li>Partner with internal teams to gather business requirements and map workflows</li>
                  <li>Collaborate with engineering teams and external vendors to integrate business systems</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-date">Oct 2022 - Mar 2025</div>
              <div className="timeline-content">
                <h3 className="job-title">Services Readiness Team Lead</h3>
                <p className="job-description">
                  Led the Services Readiness team responsible for onboarding all new services customers by completing initial configuration and testing of technology.
                </p>
                <ul className="job-achievements">
                  <li>Ensured both internal and external customers are educated and prepared for production</li>
                  <li>Managed technology configuration and testing processes for new service implementations</li>
                  <li>Developed comprehensive onboarding procedures for seamless customer transitions</li>
                  <li>Maintained high standards for service readiness and customer satisfaction</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-date">Jul 2021 - Sep 2022</div>
              <div className="timeline-content">
                <h3 className="job-title">People Development Coach</h3>
                <p className="company-name">Asurion</p>
                <p className="job-description">
                  Responsible for coaching and development of a cross-functional team overseeing 5 markets in 3 states for Asurion's Major Appliance repair program.
                </p>
                <ul className="job-achievements">
                  <li>Coach of the Quarter Q4-2020 and Q1-2022</li>
                  <li>Piloted and launched a nationwide program</li>
                  <li>Provided Process Improvement feedback to Directors and Sr. Directors</li>
                  <li>Established trackable performance metrics for experts</li>
                  <li>Assisted with training and preparing new leadership for market launches</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-date">Feb 2020 - Jul 2021</div>
              <div className="timeline-content">
                <h3 className="job-title">Service and Repair Leadership</h3>
                <p className="company-name">Asurion</p>
                <p className="job-description">
                  Led and developed cell phone repair technicians in multiple stores under the AT&T partnership. Assisted with the AT&T / Asurion launch in markets.
                </p>
                <ul className="job-achievements">
                  <li>Cultivated high performance environment for meeting and exceeding client expectations</li>
                  <li>Worked with Senior leadership to establish repair standards and processes</li>
                  <li>Lowered damaged beyond repair rate while increasing successful repair metrics</li>
                  <li>Improved repair performance across multiple markets on the east coast</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-date">May 2016 - Feb 2020</div>
              <div className="timeline-content">
                <h3 className="job-title">Process Improvement Manager</h3>
                <p className="company-name">Gadgetfix</p>
                <p className="job-description">
                  Managed repair center operations with various locations in the Jacksonville, FL market performing repairs on mobile devices, phones, tablets, and computers.
                </p>
                <ul className="job-achievements">
                  <li>Established repair guidelines and SOP's based on Apple and Samsung repair standards</li>
                  <li>Developed internal training certification for repair experts resulting in Apple Certified repair experts</li>
                  <li>Created post-repair follow-up practices and ongoing customer service training</li>
                  <li>Worked with clients to establish new service areas and processes for expansion</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-date">Sep 2013 - May 2016</div>
              <div className="timeline-content">
                <h3 className="job-title">Systems Development Manager</h3>
                <p className="company-name">Gadgetfix</p>
                <p className="job-description">
                  Led software development projects working with cross-functional teams both US and internationally for various technology initiatives.
                </p>
                <ul className="job-achievements">
                  <li>Developed and tested internal Customer Management Systems (CMS) used across locations</li>
                  <li>Created company Point of Sale systems with integration into the CMS</li>
                  <li>Established a Learning Management System (LMS) for training and onboarding</li>
                  <li>Managed software development lifecycle for multiple technology projects</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-date">2011 - 2013</div>
              <div className="timeline-content">
                <h3 className="job-title">Operations Manager</h3>
                <p className="company-name">SupportSpace, Ltd.</p>
                <p className="job-description">
                  IT Operations Manager for SupportSpace providing outsourced remote technical support to clients such as GeekSquad, Microsoft, and Staples.
                </p>
                <ul className="job-achievements">
                  <li>Managed workload for 70+ experts located remotely within the US</li>
                  <li>Created and implemented SOP's for new services to clients</li>
                  <li>Worked on pilot programs that led to contract awards with Microsoft and Staples</li>
                  <li>Met with stakeholders to identify client needs and develop service SOPs</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-date">Jun 2006 - Feb 2011</div>
              <div className="timeline-content">
                <h3 className="job-title">Project Estimator</h3>
                <p className="company-name">AM Technical Solutions, Inc</p>
                <p className="job-description">
                  Responsible for creating contract proposals and tracking workflow for various projects located within the US for Government and private sector companies.
                </p>
                <ul className="job-achievements">
                  <li>Created service proposals for Government and private sector companies</li>
                  <li>Assisted in launching a new business unit to expand current offerings</li>
                  <li>Worked with clients including Intel, Micron, and various pharmaceutical companies</li>
                  <li>Developed comprehensive project tracking and workflow management systems</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-date">Apr 2006 - Present</div>
              <div className="timeline-content">
                <h3 className="job-title">Freelance Web Developer</h3>
                <p className="company-name">Award Web Services</p>
                <p className="job-description">
                  Freelance developer and designer with a niche for making great websites and web-based apps using Power Tools and more. Experience in multiple web stack technologies.
                </p>
                <ul className="job-achievements">
                  <li>19+ years of freelance web development and design experience</li>
                  <li>Expertise in PHP, MySQL, PowerApps, PowerAutomate, and modern web technologies</li>
                  <li>Created responsive websites and web-based applications for diverse clients</li>
                  <li>Specialized in Microsoft Power Platform solutions and automation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="skills-section">
        <div className="container">
          <div className="section-title">
            <h2>Core Competencies</h2>
            <p>Technical skills and expertise areas</p>
          </div>

          <div className="skills-grid">
            <div className="skill-category">
              <h3>Web Development</h3>
              <div className="skill-tags">
                <span className="skill-tag">PHP</span>
                <span className="skill-tag">MySQL</span>
                <span className="skill-tag">HTML5</span>
                <span className="skill-tag">CSS3</span>
                <span className="skill-tag">JavaScript</span>
                <span className="skill-tag">React</span>
                <span className="skill-tag">Responsive Design</span>
                <span className="skill-tag">Web Applications</span>
              </div>
            </div>

            <div className="skill-category">
              <h3>Microsoft Power Platform</h3>
              <div className="skill-tags">
                <span className="skill-tag">PowerApps</span>
                <span className="skill-tag">PowerAutomate</span>
                <span className="skill-tag">Power BI</span>
                <span className="skill-tag">SharePoint</span>
                <span className="skill-tag">Dynamics 365</span>
                <span className="skill-tag">Workflow Automation</span>
              </div>
            </div>

            <div className="skill-category">
              <h3>Project Management</h3>
              <div className="skill-tags">
                <span className="skill-tag">Program Management</span>
                <span className="skill-tag">Process Improvement</span>
                <span className="skill-tag">Team Leadership</span>
                <span className="skill-tag">Stakeholder Management</span>
                <span className="skill-tag">Quality Assurance</span>
                <span className="skill-tag">Training & Development</span>
              </div>
            </div>

            <div className="skill-category">
              <h3>Operations & Systems</h3>
              <div className="skill-tags">
                <span className="skill-tag">Customer Management Systems</span>
                <span className="skill-tag">Point of Sale Systems</span>
                <span className="skill-tag">Learning Management Systems</span>
                <span className="skill-tag">Technical Support</span>
                <span className="skill-tag">Process Documentation</span>
                <span className="skill-tag">Performance Metrics</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resume;
